import { getFrontendConfigLoader } from '@doxyme/config';
import { getLogger } from '../../libs/logger';
import { getIsAuthSessionTelemetryEnabled } from '../../utils/authSessionTelemetryFlag';
import {
  classifyAuthHttpInitiator,
  FRONTEGG_TRACE_ID_HEADER,
  recordFronteggHttp,
  type FronteggHttpRecord,
} from './authSessionTelemetry';

const getFronteggTraceIdFromResponse = (
  response: Response,
): string | undefined => {
  const traceId = response.headers.get(FRONTEGG_TRACE_ID_HEADER)?.trim();
  return traceId ? traceId : undefined;
};

const logger = getLogger('installFronteggFetchTelemetry');

const FRONTEGG_AUTH_PATH_PATTERNS = [
  /\/oauth\/token(?:\/|$|\?)/i,
  /\/silent(?:\/|$|\?)/i,
  /\/refresh(?:\/|$|\?)/i,
  /\/user\/token\/refresh(?:\/|$|\?)/i,
  /\/identity\/resources\/auth\/v\d+\/user\/token\/refresh(?:\/|$|\?)/i,
  /\/identity\/resources\/auth\/v\d+\/user\/token(?:\/|$|\?)/i,
];

let isInstalled = false;
let originalFetch: typeof fetch | null = null;

const sanitizeUrlForLogging = (
  url: string,
): {
  urlPath: string;
  queryParamKeys: string[];
} => {
  try {
    const parsed = new URL(url, window.location.origin);
    const queryParamKeys = [...parsed.searchParams.keys()];
    return {
      urlPath: `${parsed.pathname}`,
      queryParamKeys,
    };
  } catch {
    return { urlPath: url.split('?')[0] ?? url, queryParamKeys: [] };
  }
};

const parseJsonErrorFields = async (
  response: Response,
): Promise<Pick<FronteggHttpRecord, 'errorCode' | 'errorMessage'>> => {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return {};
  }

  try {
    const cloned = response.clone();
    const body = (await cloned.json()) as Record<string, unknown>;
    const nestedError =
      typeof body['error'] === 'object' && body['error'] !== null
        ? (body['error'] as Record<string, unknown>)
        : null;

    const errorCode =
      (typeof body['code'] === 'string' ? body['code'] : undefined) ??
      (typeof body['error'] === 'string' ? body['error'] : undefined) ??
      (typeof nestedError?.['code'] === 'string'
        ? nestedError['code']
        : undefined);

    const errorMessage =
      (typeof body['message'] === 'string' ? body['message'] : undefined) ??
      (typeof nestedError?.['message'] === 'string'
        ? nestedError['message']
        : undefined);

    return { errorCode, errorMessage };
  } catch {
    return {};
  }
};

export const isFronteggAuthUrl = (url: string, baseUrl: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin);
    const base = new URL(baseUrl);
    if (parsed.host !== base.host) {
      return false;
    }
    return FRONTEGG_AUTH_PATH_PATTERNS.some((pattern) =>
      pattern.test(parsed.pathname),
    );
  } catch {
    return false;
  }
};

const hasFronteggBaseUrl = (): boolean => {
  try {
    const baseUrl = getFrontendConfigLoader().get(
      'FRONTEGG_BASE_URL',
    ) as string;
    return Boolean(baseUrl);
  } catch {
    return false;
  }
};

export const installFronteggFetchTelemetry = (): void => {
  if (isInstalled) {
    return;
  }

  if (!getIsAuthSessionTelemetryEnabled()) {
    return;
  }

  if (!hasFronteggBaseUrl()) {
    logger.info(
      'installFronteggFetchTelemetry skipped: FRONTEGG_BASE_URL unavailable',
    );
    return;
  }

  originalFetch = window.fetch.bind(window);
  const baseUrl = getFrontendConfigLoader().get('FRONTEGG_BASE_URL') as string;

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    const fetchImpl = originalFetch ?? fetch;
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;

    if (!isFronteggAuthUrl(url, baseUrl)) {
      return fetchImpl(input, init);
    }

    const startedAt = Date.now();
    const method =
      init?.method ??
      (typeof input !== 'string' && !(input instanceof URL)
        ? input.method
        : 'GET');
    const { urlPath, queryParamKeys } = sanitizeUrlForLogging(url);
    const initiator = classifyAuthHttpInitiator(startedAt);

    try {
      const response = await fetchImpl(input, init);
      const errorFields = response.ok
        ? {}
        : await parseJsonErrorFields(response);

      try {
        recordFronteggHttp({
          urlPath,
          method: method.toUpperCase(),
          status: response.status,
          ok: response.ok,
          durationMs: Date.now() - startedAt,
          initiator,
          queryParamKeys,
          fronteggTraceId: getFronteggTraceIdFromResponse(response),
          ...errorFields,
        });
      } catch {
        // telemetry must never interrupt the caller's request
      }

      return response;
    } catch (error) {
      const normalized =
        error instanceof Error
          ? { errorMessage: error.message }
          : { errorMessage: String(error) };

      try {
        recordFronteggHttp({
          urlPath,
          method: method.toUpperCase(),
          status: 0,
          ok: false,
          durationMs: Date.now() - startedAt,
          initiator,
          queryParamKeys,
          ...normalized,
        });
      } catch {
        // telemetry must never interrupt the caller's request
      }

      throw error;
    }
  };

  isInstalled = true;
};

/** Restores native fetch — for tests only. */
export const uninstallFronteggFetchTelemetry = (): void => {
  if (originalFetch) {
    window.fetch = originalFetch;
  }
  originalFetch = null;
  isInstalled = false;
};

export const getIsFronteggFetchTelemetryInstalled = (): boolean => isInstalled;
